<template>
  <div class="content-wrapper">
    <v-container v-if="lastPosition.features && lastPosition.features.length > 0" class="map-container">
      <v-row id="tracking-block-title" no-gutters class="block-title">
        {{ $t('device.list.item.tracking_record') }}
      </v-row>
      <l-map
        id="tracking-map"
        ref="last-position-map"
        v-resize="onResize"
        :padding="padding"
        :options="{ preferCanvas: true, minZoom: 3 }"
        @ready="onMapReady"
      >
        <l-tile-layer v-bind="tilesOpts" />
        <l-control position="topleft" class="leaflet-bar leaflet-control">
          <a>
            <v-icon class="leaflet-center">mdi-fit-to-page-outline</v-icon>
          </a>
        </l-control>
      </l-map>
      <v-col class="tracking-records-col see-more tracking-records">
        <div>
          <a id="seemore-tracking" @click="$emit('trackingRecords')">
            {{ $t('common.see_more') }}
          </a>
        </div>
      </v-col>
    </v-container>
    <v-container v-else>
      <v-row id="tracking-block-title" no-gutters class="block-title">
        {{ $t('device.list.item.tracking_record') }}
      </v-row>
      <v-row no-gutters class="lastpos-wrapper">
        <span id="tracking-no-record">{{ $t('device.list.item.no_tracking_record') }}</span>
        <img
          v-if="!loading"
          id="tracking-placeholder"
          :src="require('@/assets/images/overview-world.svg')"
          alt="overview-world"
        >
      </v-row>
    </v-container>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import moment from 'moment';
import { LControl, LMap, LTileLayer, LTooltip } from 'vue2-leaflet';
import L, { circleMarker, LatLngTuple, PointExpression } from 'leaflet';
import { colors } from '@/constants/theme/colors';
import { MAP_ATTRIBUTION, TILES_URL } from '@/constants/map/mapConstants';

@Component({
  components: {
    LMap,
    LTileLayer,
    LControl,
    LTooltip
  }
})
export default class LastPosition extends Vue {
  @Prop()
  lastPosition!: any;

  private geoGroup: L.FeatureGroup;

  private layers: L.Layer[] = [];

  private padding: PointExpression = [25, 25];

  private map: L.Map;

  private tilesOpts = {
    url: TILES_URL,
    attribution: MAP_ATTRIBUTION
  };

  private markerOpts = {
    radius: 4,
    fillOpacity: 1,
    weight: 1,
    color: colors.secondaryDarker,
    fillColor: colors.secondary
  };

  private onResize() {
    if (this.map) this.map.invalidateSize();
  }

  private onMapReady() {
    const mapComponent = this.$refs['last-position-map'] as LMap;
    this.map = mapComponent.mapObject;
    const markerLayer = new L.FeatureGroup();
    // Back returns Lon/Lat format, need to reverse for LatLngTuple
    const point: LatLngTuple = [this.lastPosition.features[0].geometry.coordinates[1], this.lastPosition.features[0].geometry.coordinates[0]];
    const props = this.lastPosition.features[0].properties;
    const marker = circleMarker(point, this.markerOpts);

    const tooltipDOMString = `
      <div class="tooltip">
        <div>
          <span id="last-pos-title" class="title">
            ${this.$t('device.tracking.last_pos')}
          </span>
        </div>
        <div class="mt-2">
          <span id="last-pos-time" style="font-weight: bold;">${this.$t('device.tracking.time')} : </span><span>${moment(props.timestamp * 1000).format('DD/MM/YYYY HH:mm:ss')}</span>
        </div>
        <div class="mt-2">
          <span id="last-pos-position" style="font-weight: bold;">${this.$t('device.tracking.position')} : </span><span>Lon: ${point[1]}°, Lat: ${point[0]}° </span>
        </div>
        <div class="mt-2">
          <span id="last-pos-hdop" style="font-weight: bold;">${this.$t('device.tracking.hdop')} : </span><span>${props.hdop}</span>
        </div>
        <div class="mt-2">
          <span id="last-pos-cellid" style="font-weight: bold;">${this.$t('device.tracking.cellid')} : </span><span>${props.cellID}</span>
        </div>
        <div class="mt-2">
          <span id="last-pos-elevation" style="font-weight: bold;">${this.$t('device.tracking.elevation')} : </span><span> ${props.elevation} meter${Math.abs(props.elevation) > 1 ? 's' : ''}</span>
        </div>
        <div class="mt-2">
          <span id="last-pos-heading" style="font-weight: bold;">${this.$t('device.tracking.heading')} : </span><span>${props.heading}°</span>
        </div>
        <div class="mt-2">
          <span id="last-pos-speed" style="font-weight: bold;">${this.$t('device.tracking.speed')} : </span><span>${props.speed} km/h</span>
        </div>
        <div class="mt-2">
          <span id="last-pos-obestatus" style="font-weight: bold;">${this.$t('device.tracking.obe_status')} : </span><span>${props.obeStatus} </span>
        </div>
        <div class="mt-2">
          <span id="last-pos-tollingState" style="font-weight: bold;">${this.$t('device.tracking.tolling_state')} : </span><span>${props.tollingState} </span>
        </div>
        <div class="mt-2">
          <span id="last-pos-exemption" style="font-weight: bold;">${this.$t('device.tracking.exemption')} : </span><span>${props.exemption} </span>
        </div>
        <div class="mt-2">
          <span id="last-pos-private-mode" style="font-weight: bold;">${this.$t('device.tracking.private_mode')} : </span><span>${props.privateMode} </span>
        </div>
        <div class="mt-2">
          <span id="last-pos-last_counter" style="font-weight: bold;">${this.$t('device.tracking.last_position_counter')} : </span><span>${props.lastCounter}</span>
        </div>
        <div class="mt-2">
          <span id="last-pos-counter" style="font-weight: bold;">${this.$t('device.tracking.pos_counter')} : </span><span>${props.counter}</span>
        </div>
      </div>
    `;
    marker.bindPopup(tooltipDOMString);

    markerLayer.addLayer(marker);
    markerLayer.addTo(this.map);
    this.map.fitBounds(markerLayer.getBounds(), {
      maxZoom: 8, padding: this.padding
    });
  }
}
</script>
<style lang="scss" scoped>
@import './styles/last-position.scss';
</style>
